import { Alert, Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveAccountState } from '../api/captive';
import { TopUpRoutes } from './TopUpRoutes';
import { absMoney, formatRubles, isNegativeMoney } from './format';

/**
 * Financial-block screen (Amendment 02 Part A): a financially suspended account is
 * routed to replenishment, not to device registration — the device was already bound
 * before this screen showed, which is what makes access open by itself once the
 * account is paid up.
 *
 * It now carries the SAME three routes out as the activation screen (card stub,
 * office/ОСМП, refresh) via TopUpRoutes. Before that it had none: only a sentence
 * pointing at a Personal Area that does not exist until Phase 7 — no payment entry,
 * no re-poll, not even a hint that paying in the office works. A suspended
 * subscriber is the single most likely visitor of this portal, so "no route out" was
 * the most frequently hit dead end in the flow.
 *
 * The debt is named when the balance is negative (that is the amount to pay);
 * otherwise just the balance is shown. Both come from the core as decimal strings and
 * are never parsed into floats.
 */
export function BlockedScreen({
  account,
  registrationConfirmed = true,
  refreshing,
  topUpPending,
  topUpNotice,
  onTopUp,
  onRefresh,
  onRelogin,
}: {
  /** The account-state block, when the portal has one (it always does here). */
  account: CaptiveAccountState | null;
  /**
   * ⚠ FALSE SINCE D408 (production 2026-09-01): this screen is ALSO reached from a
   * registration that FAILED. A suspended subscriber in the walled garden holds his
   * ordinary PUBLIC address, so `POST /captive/register` answers `404
   * captive.context_not_found` and nothing is bound — and the flow now routes him
   * here on the state the login already reported, instead of dead-ending him on
   * «не найдена активная сессия захвата … перезагрузите устройство».
   *
   * The closing line of this screen is the one that has to change with it: telling
   * him his device is already registered when no binding was written is a lie, and a
   * comfortable lie on a walled-garden page is what the fix is FOR. The honest
   * variant says the registration was not confirmed, that it does not stand in the
   * way of the thing he must actually do (pay), and what to do if access does not
   * open afterwards.
   */
  registrationConfirmed?: boolean;
  refreshing: boolean;
  topUpPending: boolean;
  topUpNotice: string | null;
  onTopUp: () => void;
  onRefresh: () => void;
  onRelogin: () => void;
}) {
  const { t } = useTranslation();
  const inDebt = isNegativeMoney(account?.balance);
  return (
    <Result
      status="warning"
      title={t('blocked.title')}
      subTitle={t('blocked.body')}
      style={{ padding: '8px 0' }}
      extra={[
        <Button type="link" key="relogin" onClick={onRelogin}>
          {t('blocked.relogin')}
        </Button>,
      ]}
    >
      {account && (
        <div className="identity-panel" style={{ marginBottom: 16, textAlign: 'left' }}>
          <div className="identity-row">
            <span className="k">{inDebt ? t('blocked.debt') : t('blocked.balance')}</span>
            <span className={inDebt ? 'v money money-alert' : 'v money'}>
              {formatRubles(inDebt ? absMoney(account.balance) : account.balance)}
            </span>
          </div>
        </div>
      )}
      <Alert type="info" showIcon message={t('blocked.note')} style={{ marginBottom: 16 }} />
      <TopUpRoutes
        topUpPending={topUpPending}
        refreshing={refreshing}
        topUpNotice={topUpNotice}
        onTopUp={onTopUp}
        onRefresh={onRefresh}
      />
      <p className="portal-hint" style={{ margin: '16px 0 0', textAlign: 'center' }}>
        {t(registrationConfirmed ? 'blocked.deviceRegistered' : 'blocked.deviceUnconfirmed')}
      </p>
    </Result>
  );
}
